import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3ed986d0"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["onWheel"]
const _hoisted_2 = {
  class: "external-image-magnifier",
  "aria-hidden": "true"
}
const _hoisted_3 = {
  key: 0,
  class: "external-image-status",
  role: "status"
}
const _hoisted_4 = { class: "external-image-toolbar" }
const _hoisted_5 = { class: "external-image-menu" }
const _hoisted_6 = ["aria-expanded"]
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "external-image-title-caret",
  "aria-hidden": "true"
}, "▾", -1))
const _hoisted_8 = {
  key: 0,
  class: "external-image-list"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { "aria-hidden": "true" }, "×", -1))
const _hoisted_11 = [
  _hoisted_10
]

import * as THREE from "three";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { CustomViewer } from "@/classes/viewer/custom-viewer";
import type { ExternalImage } from "@/classes/viewer/external-image";
import { isCadTool } from "@/classes/viewer/tools";
import { useAppStateStore } from "@/store/app-state";


export default /*#__PURE__*/_defineComponent({
  __name: 'ExternalImageControls',
  props: {
    image: null,
    viewer: null
  },
  emits: ["close", "select"],
  setup(__props: any, { emit }: { emit: ({ (event: "close"): void; (event: "select", image: ExternalImage): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { image: ExternalImage; viewer: CustomViewer };



const appState = useAppStateStore();
const isImageListOpen = ref(false);
const loadState = ref<"loading" | "loaded" | "error">("loading");
const displayedImage = ref<ExternalImage | null>(null);
const showLoading = ref(false);
let loadingTimer: ReturnType<typeof setTimeout> | undefined;
const isCadCursorActive = ref(false);
const magnifierCanvas = ref<HTMLCanvasElement | null>(null);
const interactionArea = ref<HTMLElement | null>(null);
const images = computed(() => externalImageManager().getImages());
const clickTolerance = 4;
const rotationSensitivity = 0.12;
const minPitch = -85;
const maxPitch = 85;
const minFov = 35;
const maxFov = 95;
const fovWheelSensitivity = 0.04;
const opacityWheelSensitivity = 0.001;
const minExternalImageOpacity = 0.1;
const maxExternalImageOpacity = 1;

let isDragging = false;
let pointerStartX = 0;
let pointerStartY = 0;
let previousX = 0;
let previousY = 0;
let lon = 0;
let lat = 0;
let fov = props.viewer.getFOV();

function externalImageManager() {
	return props.viewer.externalImageManager;
}

function updateCadCursor() {
	isCadCursorActive.value = isCadTool(appState.selectedTool);
}

function activatePointer() {
	updateCadCursor();
	externalImageManager().magnifier.setPointerActive(true);
}

function shouldRotateImage(event: PointerEvent): boolean {
	return !props.viewer.isCadDragTargetUnderPointer(event.clientX, event.clientY);
}

function didDrag(event: PointerEvent): boolean {
	return Math.hypot(event.clientX - pointerStartX, event.clientY - pointerStartY) > clickTolerance;
}

function syncView() {
	lat = THREE.MathUtils.clamp(lat, minPitch, maxPitch);
	externalImageManager().updateView(props.image, lon, lat, fov);
}

function onPointerDown(event: PointerEvent) {
	if (!externalImageManager().isReady) return;
	interactionArea.value?.focus({ preventScroll: true });
	isDragging = shouldRotateImage(event);
	isImageListOpen.value = false;
	externalImageManager().clearMarkerHover();
	pointerStartX = previousX = event.clientX;
	pointerStartY = previousY = event.clientY;
}

function onPointerMove(event: PointerEvent) {
	activatePointer();
	if (!isDragging) {
		externalImageManager().hoverMarker(event.clientX, event.clientY, props.image);
		return;
	}

	externalImageManager().clearMarkerHover();
	lon -= (event.clientX - previousX) * rotationSensitivity;
	lat -= (event.clientY - previousY) * rotationSensitivity;
	previousX = event.clientX;
	previousY = event.clientY;
	syncView();
}

function onPointerUp(event: PointerEvent) {
	if (!externalImageManager().isReady) return;
	isDragging = false;
	if (didDrag(event)) {
		props.viewer.suppressCurrentPointerClick();
		return;
	}
	if (event.button !== 0) return;
	const target = externalImageManager().selectMarker(event.clientX, event.clientY, props.image);
	if (target) {
		props.viewer.suppressCurrentPointerClick();
		emit("select", target);
	}
}

function onPointerLeave() {
	if (isDragging) props.viewer.suppressCurrentPointerClick();
	isDragging = false;
	externalImageManager().magnifier.setPointerActive(false);
	externalImageManager().clearMarkerHover();
}

function onWheel(event: WheelEvent) {
	if (!externalImageManager().isReady) return;
	if (event.shiftKey) {
		const scrollDelta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
		const externalImageOpacity = THREE.MathUtils.clamp(
			externalImageManager().getImageOpacity() - scrollDelta * opacityWheelSensitivity,
			minExternalImageOpacity,
			maxExternalImageOpacity,
		);
		externalImageManager().setImageOpacity(externalImageOpacity);
		return;
	}
	fov = THREE.MathUtils.clamp(fov + event.deltaY * fovWheelSensitivity, minFov, maxFov);
	syncView();
}

function onKeyDown(event: KeyboardEvent) {
	if (!["Escape", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
	event.preventDefault();
	if (event.key === "Escape") return isImageListOpen.value ? isImageListOpen.value = false : emit("close");
	selectAdjacentImage(event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1);
}

function selectImage(image: ExternalImage) {
	isImageListOpen.value = false;
	if (image.uuid !== props.image.uuid) {
		emit("select", image);
	}
}

function selectAdjacentImage(direction: -1 | 1) {
	const image = externalImageManager().getAdjacentImage(props.image, direction);
	if (image) {
		emit("select", image);
	}
}

function enterImage(image: ExternalImage) {
	isImageListOpen.value = false;
	const worldDirection = props.viewer.scene.view.direction.clone();
	const localOrientation = externalImageManager().getLocalOrientation(image, worldDirection);
	lon = localOrientation.yawDegrees;
	lat = localOrientation.pitchDegrees;
	isDragging = false;
	loadState.value = "loading";
	clearTimeout(loadingTimer);
	showLoading.value = false;
	loadingTimer = setTimeout(() => showLoading.value = true, 400);
	externalImageManager().enter(image, worldDirection, (state) => {
		clearTimeout(loadingTimer);
		showLoading.value = false;
		loadState.value = state;
		if (state === "loaded") displayedImage.value = image;
	});
}

onMounted(() => {
	externalImageManager().setInteractionElement(interactionArea.value);
	externalImageManager().magnifier.setTarget(magnifierCanvas.value);
	enterImage(props.image);
	interactionArea.value?.focus({ preventScroll: true });
});

watch(() => props.image, enterImage);

onBeforeUnmount(() => {
	clearTimeout(loadingTimer);
	externalImageManager().setInteractionElement(null);
	externalImageManager().clearMarkerHover();
	externalImageManager().exit();
	externalImageManager().magnifier.setTarget(null);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "external-image-controls",
    onKeydown: onKeyDown
  }, [
    _createElementVNode("div", {
      ref_key: "interactionArea",
      ref: interactionArea,
      tabindex: "0",
      class: _normalizeClass(["external-image-interaction-area", { 'cad-active': isCadCursorActive.value }]),
      onPointerdown: onPointerDown,
      onPointerenter: activatePointer,
      onPointermove: onPointerMove,
      onPointerup: onPointerUp,
      onPointerleave: onPointerLeave,
      onWheel: _withModifiers(onWheel, ["prevent"])
    }, null, 42, _hoisted_1),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("canvas", {
        ref_key: "magnifierCanvas",
        ref: magnifierCanvas,
        class: "external-image-magnifier-canvas"
      }, null, 512)
    ]),
    (loadState.value === 'error' || showLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(loadState.value === "loading" ? "Loading image…" : "Unable to load image. Select another image or close this view."), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("button", {
          class: "external-image-title",
          type: "button",
          "aria-expanded": isImageListOpen.value,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (isImageListOpen.value = !isImageListOpen.value))
        }, [
          _createElementVNode("span", null, _toDisplayString(displayedImage.value?.name ?? __props.image.name), 1),
          _hoisted_7
        ], 8, _hoisted_6),
        (isImageListOpen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(images), (entry) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: entry.uuid,
                  type: "button",
                  class: _normalizeClass(["external-image-list-item", { active: entry.uuid === displayedImage.value?.uuid }]),
                  onClick: ($event: any) => (selectImage(entry))
                }, _toDisplayString(entry.name), 11, _hoisted_9))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("button", {
        class: "toolbar-btn",
        title: "Close image",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
      }, _hoisted_11)
    ])
  ], 32))
}
}

})